import {
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPE_SHORT_LABELS,
  duplicateContextOrderMessage,
  duplicateDocumentKeyMessage,
  invalidContextOrderMessage,
  noApplicableContextMessage,
  unsafeDocumentKeyMessage,
} from "./documents-copy";

export const DOCUMENT_CONTEXTS = ["batch", "order"] as const;
export type DocumentContext = (typeof DOCUMENT_CONTEXTS)[number];

export type DocumentCatalogDefinition<TKey extends string = string> = {
  key: TKey;
  label: string;
  shortLabel?: string;
  filename: string;
  downloadSuffix: string;
  commercial: boolean;
  order: Partial<Record<DocumentContext, number>>;
};

type KeyForContext<
  TDefinitions extends readonly DocumentCatalogDefinition[],
  TContext extends DocumentContext,
> = TDefinitions[number] extends infer TDefinition
  ? TDefinition extends { key: infer TKey extends string; order: infer TOrder }
    ? TContext extends keyof TOrder ? TKey : never
    : never
  : never;

export type ResolvedDocumentCatalogEntry<TKey extends string = string> = {
  key: TKey;
  label: string;
  shortLabel: string;
  filename: string;
  downloadSuffix: string;
  commercial: boolean;
};

const SAFE_DOCUMENT_KEY = /^[A-Za-z][A-Za-z0-9_]*$/;

export function createDocumentCatalog<const TDefinitions extends readonly DocumentCatalogDefinition[]>(
  definitions: TDefinitions,
) {
  const keys = new Set<string>();
  const domainOrders: Record<DocumentContext, Set<number>> = {
    batch: new Set(),
    order: new Set(),
  };

  for (const definition of definitions) {
    if (!SAFE_DOCUMENT_KEY.test(definition.key)) {
      throw new Error(unsafeDocumentKeyMessage(definition.key));
    }
    if (keys.has(definition.key)) {
      throw new Error(duplicateDocumentKeyMessage(definition.key));
    }
    keys.add(definition.key);

    const applicableContexts = DOCUMENT_CONTEXTS.filter((context) => definition.order[context] != null);
    if (applicableContexts.length === 0) {
      throw new Error(noApplicableContextMessage(definition.key));
    }
    for (const context of applicableContexts) {
      const position = definition.order[context]!;
      if (!Number.isInteger(position) || position < 0) {
        throw new Error(invalidContextOrderMessage(context, definition.key));
      }
      if (domainOrders[context].has(position)) {
        throw new Error(duplicateContextOrderMessage(context, position));
      }
      domainOrders[context].add(position);
    }
  }

  const resolved = definitions.map((definition) => ({
    ...definition,
    shortLabel: definition.shortLabel ?? definition.label,
  }));

  function list<TContext extends DocumentContext>(context: TContext) {
    return resolved
      .filter((entry) => entry.order[context] != null)
      .sort((left, right) => left.order[context]! - right.order[context]!) as unknown as Array<
        ResolvedDocumentCatalogEntry<KeyForContext<TDefinitions, TContext>>
      >;
  }

  function resolve<TContext extends DocumentContext>(context: TContext, key: string) {
    return list(context).find((entry) => entry.key === key);
  }

  function isValid<TContext extends DocumentContext>(
    context: TContext,
    key: string,
  ): key is KeyForContext<TDefinitions, TContext> {
    return resolve(context, key) != null;
  }

  function search<TContext extends DocumentContext>(context: TContext, query: string) {
    const normalized = query.trim().toLocaleLowerCase();
    if (!normalized) return list(context);
    return list(context).filter((entry) => entry.label.toLocaleLowerCase().includes(normalized));
  }

  function isCommercial(key: string): boolean {
    return resolved.find((entry) => entry.key === key)?.commercial ?? true;
  }

  function listWithoutMoney<TContext extends DocumentContext>(context: TContext) {
    return list(context).filter((entry) => !entry.commercial);
  }

  return { list, resolve, isValid, search, isCommercial, listWithoutMoney };
}

const DOCUMENT_DEFINITIONS = [
  {
    key: "certificate_of_analysis",
    label: DOCUMENT_TYPE_LABELS.certificate_of_analysis,
    filename: "certificate_of_analysis.pdf",
    downloadSuffix: "coa",
    commercial: false,
    order: { batch: 0 },
  },
  {
    key: "specification",
    label: DOCUMENT_TYPE_LABELS.specification,
    shortLabel: DOCUMENT_TYPE_SHORT_LABELS.specification,
    filename: "specification.pdf",
    downloadSuffix: "specs",
    commercial: false,
    order: { batch: 1 },
  },
  {
    key: "supplier_coa",
    label: DOCUMENT_TYPE_LABELS.supplier_coa,
    filename: "supplier_coa.pdf",
    downloadSuffix: "supplier_coa",
    commercial: false,
    order: { batch: 2 },
  },
  {
    key: "gc",
    label: DOCUMENT_TYPE_LABELS.gc,
    filename: "gc.pdf",
    downloadSuffix: "gc",
    commercial: false,
    order: { batch: 3, order: 7 },
  },
  {
    key: "nmr",
    label: DOCUMENT_TYPE_LABELS.nmr,
    filename: "nmr.pdf",
    downloadSuffix: "nmr",
    commercial: false,
    order: { batch: 4, order: 9 },
  },
  {
    key: "hplc",
    label: DOCUMENT_TYPE_LABELS.hplc,
    filename: "hplc.pdf",
    downloadSuffix: "hplc",
    commercial: false,
    order: { batch: 5, order: 8 },
  },
  {
    key: "ir",
    label: DOCUMENT_TYPE_LABELS.ir,
    filename: "ir.pdf",
    downloadSuffix: "ir",
    commercial: false,
    order: { batch: 6, order: 10 },
  },
  {
    key: "msds",
    label: DOCUMENT_TYPE_LABELS.msds,
    filename: "msds.pdf",
    downloadSuffix: "msds",
    commercial: false,
    order: { batch: 7, order: 5 },
  },
  {
    key: "product_origin",
    label: DOCUMENT_TYPE_LABELS.product_origin,
    shortLabel: DOCUMENT_TYPE_SHORT_LABELS.product_origin,
    filename: "product_origin.pdf",
    downloadSuffix: "product_origin",
    commercial: false,
    order: { batch: 8 },
  },
  {
    key: "quote",
    label: DOCUMENT_TYPE_LABELS.quote,
    filename: "quote.pdf",
    downloadSuffix: "quote",
    commercial: true,
    order: { order: 0 },
  },
  {
    key: "correspondence",
    label: DOCUMENT_TYPE_LABELS.correspondence,
    filename: "correspondence.pdf",
    downloadSuffix: "correspondence",
    commercial: false,
    order: { order: 1 },
  },
  {
    key: "poDocument",
    label: DOCUMENT_TYPE_LABELS.poDocument,
    filename: "po_document.pdf",
    downloadSuffix: "po",
    commercial: true,
    order: { order: 2 },
  },
  {
    key: "invoice",
    label: DOCUMENT_TYPE_LABELS.invoice,
    filename: "invoice.pdf",
    downloadSuffix: "invoice",
    commercial: true,
    order: { order: 3 },
  },
  {
    key: "coa",
    label: DOCUMENT_TYPE_LABELS.coa,
    filename: "coa.pdf",
    downloadSuffix: "coa",
    commercial: false,
    order: { order: 4 },
  },
  {
    key: "shippingDocument",
    label: DOCUMENT_TYPE_LABELS.shippingDocument,
    filename: "shipping_document.pdf",
    downloadSuffix: "shipping",
    commercial: false,
    order: { order: 6 },
  },
  {
    key: "creditMemo",
    label: DOCUMENT_TYPE_LABELS.creditMemo,
    filename: "credit_memo.pdf",
    downloadSuffix: "credit_memo",
    commercial: true,
    order: { order: 11 },
  },
  {
    key: "countryOfOrigin",
    label: DOCUMENT_TYPE_LABELS.countryOfOrigin,
    filename: "country_of_origin.pdf",
    downloadSuffix: "country_of_origin",
    commercial: false,
    order: { order: 12 },
  },
  {
    key: "miscellaneous",
    label: DOCUMENT_TYPE_LABELS.miscellaneous,
    filename: "miscellaneous.pdf",
    downloadSuffix: "misc",
    commercial: false,
    order: { batch: 9, order: 13 },
  },
  {
    key: "packingSlip",
    label: DOCUMENT_TYPE_LABELS.packingSlip,
    filename: "packing_slip.pdf",
    downloadSuffix: "packing_slip",
    commercial: false,
    order: { order: 14 },
  },
  {
    key: "releaseOrder",
    label: DOCUMENT_TYPE_LABELS.releaseOrder,
    filename: "release_order.pdf",
    downloadSuffix: "release_order",
    commercial: false,
    order: { order: 15 },
  },
] as const satisfies readonly DocumentCatalogDefinition[];

export type DocumentTypeFor<TContext extends DocumentContext> = KeyForContext<
  typeof DOCUMENT_DEFINITIONS,
  TContext
>;

export const DOCUMENT_CATALOG = createDocumentCatalog(DOCUMENT_DEFINITIONS);
